// src/lib/apiEdgeFailover.js
//
// Fallback same-origin → Cloud Run quando a borda do Firebase Hosting (Fastly)
// cai. Em produção o build usa window.location.origin como base da API, então
// TODA chamada passa pelo rewrite do Hosting. Incidente 23/09/2026 10:49 BRT:
// ~5 min de "503 No healthy backends" (Varnish) com o apiv2 saudável — o app
// parou mesmo com o backend no ar.
//
// Regra de segurança (evita processar duas vezes no backend):
//   - Resposta que chegou ao apiV2 sempre traz X-Cloud-Trace-Context; erro
//     gerado pela borda não traz. Só repetimos quando o header está ausente.
//   - GET/HEAD/OPTIONS: repete em qualquer 5xx da borda ou erro de rede.
//   - Demais métodos: só em 503 com Server: Varnish (a borda recusou antes de
//     encaminhar). Erro de rede em POST não repete — não dá para saber se chegou.
//   - Depois de um retry direto bem-sucedido, as próximas chamadas vão direto
//     ao Cloud Run por DIRECT_MODE_MS (inclusive POST), e então voltam sozinhas.

const DIRECT_ORIGIN = 'https://apiv2-dfmpwvw5uq-uc.a.run.app';
const DIRECT_MODE_MS = 5 * 60 * 1000;
const DIRECT_MODE_KEY = 'api_edge_failover_until';

const PRODUCTION_HOSTS = new Set([
  'consultamais.app',
  'www.consultamais.app',
  'consultamaissimplificada.web.app',
  'consultamaissimplificada.firebaseapp.com',
]);

// Espelho dos rewrites "function": "apiV2" do firebase.json (target app).
const API_EXACT_PATHS = new Set([
  '/stream/webhook', '/email/inbound', '/email/unsubscribe', '/email/send',
  '/health', '/gpt', '/token', '/register_token', '/test_push', '/test_promo',
  '/version-config', '/attribution/save',
  '/chat/schedule-intro', '/chat/message-recorded', '/chat/link-check',
  '/fastcheck/create', '/fastcheck/status', '/easycheck/create', '/easycheck/status',
  '/admin/update-patient-name', '/admin/prontuarios/repair-names', '/admin/sync-messages',
  '/admin/repair-patient-data', '/admin/send-whatsapp-notice',
  '/admin/send-whatsapp-restoration-notice', '/admin/send-autenticar-notice',
  '/admin/send-amigo-launch', '/admin/coupons/stats',
  '/admin/identity/backfill-canonical-names', '/admin/regenerate-prontuario',
  '/admin/send-weekly-email-now', '/admin/scheduled-reminders', '/admin/security/snapshot',
]);
const API_PREFIXES = [
  '/webhooks/', '/gpt/', '/medical-check/', '/diagnostics/', '/oauth/', '/payments/',
  '/coupons/', '/referral/', '/prescreve/', '/questions/', '/doctor/', '/patient/',
  '/nfse/', '/whatsapp/', '/support/', '/research/', '/d/', '/doc-preview/',
  '/registration/', '/prontuario/', '/triage/', '/memed/', '/vidaas/', '/docs/',
  '/admin/payments/', '/admin/ai-audit/', '/admin/scheduled-reminders/', '/admin/fastcheck/',
];

// CORS do apiV2 (functions/index.js, allowedHeaders) + headers CORS-safelisted.
const CORS_ALLOWED_HEADERS = new Set([
  'content-type', 'authorization', 'x-admin-token', 'x-firebase-appcheck',
  'accept', 'accept-language', 'content-language',
]);
// Dicas de cache que não passam no preflight; o retry usa cache: 'no-store'.
const DROPPABLE_HEADERS = new Set(['cache-control', 'pragma']);

const IDEMPOTENT_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

let directUntil = 0;
let installed = false;

function isApiPath(pathname) {
  if (API_EXACT_PATHS.has(pathname)) return true;
  return API_PREFIXES.some((p) => pathname.startsWith(p));
}

function readDirectUntil() {
  if (directUntil) return directUntil;
  try { directUntil = parseInt(sessionStorage.getItem(DIRECT_MODE_KEY) || '0', 10) || 0; } catch {}
  return directUntil;
}

function isDirectModeActive() {
  return readDirectUntil() > Date.now();
}

function headerEntries(headers) {
  if (!headers) return [];
  if (typeof Headers !== 'undefined' && headers instanceof Headers) return Array.from(headers.entries());
  if (Array.isArray(headers)) return headers;
  return Object.entries(headers);
}

// Retorna o init para o Cloud Run direto, ou null se a chamada não é elegível.
function buildDirectInit(init) {
  const src = init || {};
  if (src.mode === 'same-origin' || src.mode === 'no-cors') return null;
  if (typeof ReadableStream !== 'undefined' && src.body instanceof ReadableStream) return null;
  const headers = {};
  let droppedCacheHint = false;
  for (const [rawName, value] of headerEntries(src.headers)) {
    const name = String(rawName).toLowerCase();
    if (DROPPABLE_HEADERS.has(name)) { droppedCacheHint = true; continue; }
    if (!CORS_ALLOWED_HEADERS.has(name)) return null;
    headers[name] = value;
  }
  const next = { ...src, headers, mode: 'cors' };
  if (droppedCacheHint && !src.cache) next.cache = 'no-store';
  return next;
}

function isEdgeError(res) {
  return res.status >= 500 && !res.headers.get('x-cloud-trace-context');
}

function edgeErrorIsRetryable(res, method) {
  if (IDEMPOTENT_METHODS.has(method)) return [500, 502, 503, 504].includes(res.status);
  return res.status === 503 && /varnish/i.test(res.headers.get('server') || '');
}

function isAbort(err) {
  return err && (err.name === 'AbortError' || err.name === 'TimeoutError');
}

function reportFailover(originalFetch, reason, pathname, status) {
  try {
    originalFetch(`${DIRECT_ORIGIN}/diagnostics/client-log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event: 'api_edge_failover',
        level: 'warn',
        data: { reason, path: pathname, status: status || null },
        ts: Date.now(),
        url: window.location.href,
        online: typeof navigator !== 'undefined' ? navigator.onLine : true,
        appVersion: window.APP_VERSION || 'unknown',
      }),
      keepalive: true,
    }).catch(() => {});
  } catch {}
}

function activateDirectMode(originalFetch, reason, pathname, status) {
  const wasActive = isDirectModeActive();
  directUntil = Date.now() + DIRECT_MODE_MS;
  try { sessionStorage.setItem(DIRECT_MODE_KEY, String(directUntil)); } catch {}
  if (!wasActive) {
    console.warn('[apiEdgeFailover] Borda do Hosting falhou; usando Cloud Run direto por 5 min.', reason, pathname);
    reportFailover(originalFetch, reason, pathname, status);
  }
}

export function installApiEdgeFailover() {
  if (installed || typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  if (!PRODUCTION_HOSTS.has(window.location.hostname)) return;
  installed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async function fetchWithEdgeFailover(input, init) {
    if (typeof input !== 'string' && !(input instanceof URL)) return originalFetch(input, init);

    let url;
    try { url = new URL(String(input), window.location.href); } catch { return originalFetch(input, init); }
    if (url.origin !== window.location.origin || !isApiPath(url.pathname)) return originalFetch(input, init);

    const directInit = buildDirectInit(init);
    if (!directInit) return originalFetch(input, init);

    const directUrl = `${DIRECT_ORIGIN}${url.pathname}${url.search}`;
    const method = String((init && init.method) || 'GET').toUpperCase();
    const idempotent = IDEMPOTENT_METHODS.has(method);

    if (isDirectModeActive()) {
      try {
        return await originalFetch(directUrl, directInit);
      } catch (err) {
        if (isAbort(err) || !idempotent) throw err;
        return originalFetch(input, init);
      }
    }

    let res;
    try {
      res = await originalFetch(input, init);
    } catch (err) {
      if (isAbort(err) || !idempotent) throw err;
      try {
        const direct = await originalFetch(directUrl, directInit);
        if (direct.status > 0 && direct.status < 500) {
          activateDirectMode(originalFetch, 'network_error', url.pathname, null);
        }
        return direct;
      } catch {
        throw err;
      }
    }

    if (!isEdgeError(res) || !edgeErrorIsRetryable(res, method)) return res;

    try {
      const direct = await originalFetch(directUrl, directInit);
      if (direct.status < 500) activateDirectMode(originalFetch, 'edge_5xx', url.pathname, res.status);
      return direct;
    } catch {
      return res;
    }
  };
}
